/**
 * system-view.ts — tactical in-system renderer (GALAXY §3.5.1).
 *
 * Draws the star system currently focused by UiState.focusedSystemId as a
 * bounded [0,1] × [0,1] tactical map. The UiState.systemCamera controls pan
 * and zoom; at SYSTEM_VIEW_DEFAULT_ZOOM (= 1) the whole system fits the
 * letterboxed square sub-viewport. Zooming in spreads planets and jump
 * points apart around the fixed star sprite.
 *
 * Tactical presentation scale (GALAXY §3.5.1, 2026-04-22):
 *   The tactical view is its own map — sprite sizes, sublight distances, and
 *   combat ranges live on a presentation scale independent of galaxy view. The
 *   zoom-in transition is stylised via a cross-fade rather than a literal
 *   shared world scale, so in-system content can be tuned for readability
 *   without having to "fit" on the galaxy-pixel grid.
 *
 * Layout lives in system-space coordinates [0,1]:
 *   - Star fixed at (0.5, 0.5).
 *   - Planets at their authored (systemX, systemY). Planet screen size comes
 *     from planet.size, colour from planet.biome, with an ownership halo.
 *   - Jump points on the outer ring (r ≈ 0.48) from state.jumpPoints.
 *   - Environmental placeholders (nebula blobs, asteroid clusters) seeded
 *     deterministically off the system id so they're stable per-system.
 *     These are purely visual in v1; gameplay mechanics land in a later
 *     milestone.
 *   - Ships at their per-ship (sx, sy) sublight positions. Selected player
 *     ships wear a selection ring; ships with an outstanding move order get
 *     a dashed preview line to the target.
 *
 * All screen positions flow through `worldToScreen(ui.systemCamera, …)` so
 * layout and hit-testing stay in sync.
 *
 * Pure reader — never mutates state.
 */

import type {
  BiomeKind,
  EntityId,
  EnvironmentEntity,
  GameState,
  JumpPoint,
  JumpPointId,
  Planet,
  PlanetId,
  PlanetSize,
  ShipInstance,
  ShipInstanceId,
  StructureKind,
  SystemId,
} from '../state/types';
import type { UiState } from '../ui/state';
import { marqueeBounds, viewerEmpireId } from '../ui/state';
import { worldToScreen, type Viewport } from './camera';
import { drawShip, type ShipRenderInput } from './ship-draw';
import { getBiomeTexture } from './biome-textures';
import { drawVfx, isShipInJumpIn, type VfxState } from './vfx';
import { isValidBuildSite } from '../game/commands';
import { collectTeamScannersForSystem } from '../game/scanners';
import { DEFAULT_DETECTION_BALANCE } from '../game/balance-detection';
import { effectiveDetection, effectiveStealth } from '../game/detection';
import {
  MINEFIELD_CHASSIS,
  minefieldRadius,
} from '../game/balance-space-structures';

/** Outer ring radius in system-space where jump points live (GALAXY §3.5.1). */
export const SYSTEM_BOUNDARY_RADIUS = 0.48;

/**
 * Tactical sprite sizes (screen px). Intentionally larger than their
 * galaxy-view counterparts — the system view has its own presentation scale
 * independent of the galaxy map (GALAXY §3.5.1). These numbers are tuned for
 * readability at the default camera zoom in a mid-range viewport; they don't
 * scale with camera zoom so zooming in spreads sprites apart without blowing
 * them up.
 */
const STAR_SCREEN_RADIUS = 36;
/**
 * MFix1.4 — planet draw radius (screen px) interpolated linearly from
 * 14 px at size 5 (smallest) to 28 px at size 20 (largest). Endpoints
 * match the pre-MFix1.4 small/large enum values; mid-range sizes
 * interpolate cleanly. Default home worlds (size 15) draw at ~23 px.
 */
const PLANET_SCREEN_RADIUS_MIN = 14;
const PLANET_SCREEN_RADIUS_MAX = 28;
/** Draw radius (screen px) for a jump-point diamond marker. */
const JUMP_POINT_SCREEN_RADIUS = 11;
/**
 * Chevron draw radius (screen px). The chevron itself is ~6 px from centroid;
 * this value is mostly the "half-width" used for selection-ring sizing and
 * spatial reasoning in layout code.
 */
export const SHIP_SCREEN_RADIUS = 7;
/**
 * Generous click-target radius for ship hit-tests. Bigger than the sprite so
 * the player doesn't have to pixel-hunt on a dense tactical map — tuned so a
 * ~12 px sprite lives comfortably inside a 14 px click ring.
 */
export const SHIP_HIT_RADIUS_PX = 14;

export interface SystemViewLayout {
  /** Star screen position — canonical (0.5, 0.5) run through the camera. */
  cx: number;
  cy: number;
  /** Star sprite radius in screen pixels. */
  starRadius: number;
  systemName: string;
  planets: Array<{ planet: Planet; x: number; y: number; r: number }>;
  jumpPoints: Array<{ jp: JumpPoint; x: number; y: number; r: number }>;
  /**
   * Ships rendered in this system. `x`/`y` are the chevron centroid in screen
   * space; `targetScreen` is the pre-projected move-order destination (in
   * screen space) so the renderer can draw a preview line without redoing the
   * transform.
   */
  ships: Array<{
    ship: ShipInstance;
    x: number;
    y: number;
    r: number;
    targetScreen: { x: number; y: number } | null;
  }>;
  /**
   * v3M15.1 — Environment entities (asteroid fields, gas giants, nebulae,
   * ion clouds, radiation belts, black holes) in the focused system.
   * `r` is the screen-space radius of the entity's hazard / extractor
   * footprint. Hit-testing uses this radius directly (no extra padding
   * threshold — the rendered footprints are large enough to click).
   */
  environment: Array<{ env: EnvironmentEntity; x: number; y: number; r: number }>;
  /** Screen-space radius of the r≈0.48 boundary ring. Useful for drawing. */
  boundaryRadiusScreen: number;
  /**
   * The square sub-viewport used for all layout — exposed so callers can run
   * screenToWorld against the same transform (e.g. converting a right-click
   * position into an in-system target for a move order).
   */
  squareVp: Viewport;
}

/**
 * Screen-space radius for a planet of the given size.
 * MFix1.4 — linear interpolation from min (size 5) to max (size 20).
 * `t = (size - 5) / 15` clamped to [0,1] so out-of-range sizes don't
 * over- or under-shoot. Home worlds (size 15) land at t=0.667 ≈ 23 px.
 */
function planetRadius(size: PlanetSize): number {
  const t = Math.max(0, Math.min(1, (size - 5) / 15));
  return PLANET_SCREEN_RADIUS_MIN + t * (PLANET_SCREEN_RADIUS_MAX - PLANET_SCREEN_RADIUS_MIN);
}

/**
 * Per-biome radial-gradient palette — inner (centre) brighter, outer
 * (edge) darker — per GRAPHICS §5.3. Existing v1 BiomeKind values map
 * to spec entries by closest fit:
 *   gaia → Gaia        | temperate → Tropical | desert → Desert
 *   arctic → Frozen    | ocean → Oceanic      | volcanic → Volcanic
 *   barren → Barren
 * Spec's Toxic + Radioactive aren't in the v1 enum; if PLANETS later
 * adds them, extend this table.
 */
const BIOME_GRADIENT: Readonly<Record<BiomeKind, { inner: string; outer: string }>> =
  Object.freeze({
    gaia: { inner: '#7FE5A4', outer: '#2A7050' },
    temperate: { inner: '#5FC878', outer: '#1E5C32' },
    desert: { inner: '#E4B566', outer: '#7A5020' },
    arctic: { inner: '#D6E6F2', outer: '#5C7890' },
    ocean: { inner: '#4FA9E8', outer: '#144A78' },
    volcanic: { inner: '#E85A2E', outer: '#4A140A' },
    barren: { inner: '#A79F8C', outer: '#4D463A' },
  });

/** Resolve a biome's inner / outer gradient stops. */
function biomeGradient(biome: BiomeKind): { inner: string; outer: string } {
  return BIOME_GRADIENT[biome];
}

/**
 * The shared galaxy-view `worldToScreen` scales X by viewW and Y by viewH
 * independently. That's right for the galaxy (systems authored as normalised
 * coords, rectangular map). For the tactical map [0,1]×[0,1] we want an
 * isotropic transform so the system reads as a circle, not a squash — so we
 * letterbox a square sub-viewport inside the supplied rectangle and run all
 * system-view transforms through that.
 */
export function squareViewport(vp: Viewport): Viewport {
  const size = Math.min(vp.viewW, vp.viewH);
  return {
    padLeft: vp.padLeft + (vp.viewW - size) / 2,
    padTop: vp.padTop + (vp.viewH - size) / 2,
    viewW: size,
    viewH: size,
  };
}

/**
 * Compute screen positions for all interactive elements. Returns null if
 * there's no focused system (nothing to draw or hit-test).
 */
export function layoutSystemView(
  state: GameState,
  ui: UiState,
  vp: Viewport,
): SystemViewLayout | null {
  if (ui.focusedSystemId === null) return null;
  const sys = state.systems.get(ui.focusedSystemId);
  if (!sys) return null;

  // Letterbox [0,1]×[0,1] as a square sub-viewport so circles stay circular
  // regardless of the enclosing aspect ratio.
  const squareVp = squareViewport(vp);

  // Star at system-space (0.5, 0.5); everything else flows through the
  // system camera + square viewport so pan/zoom and hit-test stay in lockstep.
  const starScreen = worldToScreen(ui.systemCamera, squareVp, { x: 0.5, y: 0.5 });

  // Boundary ring radius in screen pixels — r=0.48 system units * zoom *
  // square size (viewW and viewH match here by construction).
  const boundaryRadiusScreen =
    SYSTEM_BOUNDARY_RADIUS * ui.systemCamera.zoom * squareVp.viewW;

  // Star sprite is fixed-size (tactical presentation scale). Zooming in with
  // the system camera spreads planets + jump points apart around the star
  // rather than blowing up the star itself.
  const starRadius = STAR_SCREEN_RADIUS;

  const planets = sys.planetIds
    .map((id) => state.planets.get(id))
    .filter((p): p is Planet => !!p);

  const laidOutPlanets = planets.map((planet) => {
    const s = worldToScreen(ui.systemCamera, squareVp, {
      x: planet.systemX,
      y: planet.systemY,
    });
    return {
      planet,
      x: s.x,
      y: s.y,
      r: planetRadius(planet.size),
    };
  });

  const jps = sys.jumpPointIds
    .map((id) => state.jumpPoints.get(id))
    .filter((j): j is JumpPoint => !!j);
  const laidOutJps = jps.map((jp) => {
    const s = worldToScreen(ui.systemCamera, squareVp, { x: jp.x, y: jp.y });
    return { jp, x: s.x, y: s.y, r: JUMP_POINT_SCREEN_RADIUS };
  });

  // Ships currently in this system — each drawn at its own (sx, sy) sublight
  // position so collision-avoidance and move orders visibly matter. Mid-jump
  // ships (systemId === null) don't render here — they're interpolated on the
  // galaxy view instead.
  const shipsHere = [...state.ships.values()].filter((s) => s.systemId === sys.id);
  const laidOutShips = shipsHere.map((ship) => {
    const s = worldToScreen(ui.systemCamera, squareVp, { x: ship.sx, y: ship.sy });
    const targetScreen = ship.moveOrder
      ? worldToScreen(ui.systemCamera, squareVp, {
          x: ship.moveOrder.targetX,
          y: ship.moveOrder.targetY,
        })
      : null;
    return {
      ship,
      x: s.x,
      y: s.y,
      r: SHIP_SCREEN_RADIUS,
      targetScreen,
    };
  });

  // v3M15.1 — environment entities for hit-testing. Mirrors the
  // per-frame projection in drawEnvironmentEntities so the hit-test sees
  // exactly the screen positions the player sees.
  const laidOutEnvironment: SystemViewLayout['environment'] = [];
  for (const env of state.environment.values()) {
    if (env.systemId !== sys.id) continue;
    const s = worldToScreen(ui.systemCamera, squareVp, { x: env.sx, y: env.sy });
    const r = env.radius * ui.systemCamera.zoom * squareVp.viewW;
    laidOutEnvironment.push({ env, x: s.x, y: s.y, r });
  }

  return {
    cx: starScreen.x,
    cy: starScreen.y,
    starRadius,
    systemName: sys.name,
    planets: laidOutPlanets,
    jumpPoints: laidOutJps,
    ships: laidOutShips,
    environment: laidOutEnvironment,
    boundaryRadiusScreen,
    squareVp,
  };
}

/**
 * Hit-test a click against the linked jump points in the current system view.
 * Returns the closest in-threshold linked JP id or null. Unlinked JPs are
 * never returned because they're not jump targets.
 */
export function hitTestSystemViewJumpPoints(
  layout: SystemViewLayout,
  clickX: number,
  clickY: number,
  thresholdPx = 6,
): JumpPointId | null {
  let best: { id: JumpPointId; dist: number } | null = null;
  for (const j of layout.jumpPoints) {
    if (j.jp.linkedSystemId === null) continue;
    const d = Math.hypot(j.x - clickX, j.y - clickY);
    if (d <= j.r + thresholdPx) {
      if (!best || d < best.dist) best = { id: j.jp.id, dist: d };
    }
  }
  return best?.id ?? null;
}

/**
 * Hit-test a click against the ships in the current system view. Returns the
 * closest ship within `thresholdPx` of the click, or null. Generous by
 * default because the chevrons are tiny (~14 px threshold on a ~12 px sprite).
 */
export function hitTestSystemViewShips(
  layout: SystemViewLayout,
  clickX: number,
  clickY: number,
  thresholdPx: number = SHIP_HIT_RADIUS_PX,
): ShipInstanceId | null {
  let best: { id: ShipInstanceId; dist: number } | null = null;
  for (const s of layout.ships) {
    const d = Math.hypot(s.x - clickX, s.y - clickY);
    if (d <= thresholdPx) {
      if (!best || d < best.dist) best = { id: s.ship.id, dist: d };
    }
  }
  return best?.id ?? null;
}

/**
 * Return the ids of every ship whose screen centroid sits inside the given
 * screen-space rectangle. Used for drag-to-select marquee. Order is the same
 * as layout.ships, which is Map-insertion order — stable per frame.
 */
export function shipsInScreenRect(
  layout: SystemViewLayout,
  minX: number,
  minY: number,
  maxX: number,
  maxY: number,
): ShipInstanceId[] {
  const out: ShipInstanceId[] = [];
  for (const s of layout.ships) {
    if (s.x >= minX && s.x <= maxX && s.y >= minY && s.y <= maxY) {
      out.push(s.ship.id);
    }
  }
  return out;
}

/**
 * v3M15.1 — Hit-test a click against environment entities in the current
 * system view (asteroid fields, gas giants, nebulae, ion clouds, radiation
 * belts, black holes). Returns the closest entity whose rendered radius
 * contains the click, or null.
 *
 * Threshold padding kept smaller than planets/JPs because the rendered
 * footprints are already large; padding here would let the player select
 * environment entities they didn't click *on*.
 */
export function hitTestSystemViewEnvironment(
  layout: SystemViewLayout,
  clickX: number,
  clickY: number,
  thresholdPx = 0,
): EntityId | null {
  let best: { id: EntityId; dist: number } | null = null;
  for (const e of layout.environment) {
    const d = Math.hypot(e.x - clickX, e.y - clickY);
    if (d <= e.r + thresholdPx) {
      if (!best || d < best.dist) best = { id: e.env.id, dist: d };
    }
  }
  return best?.id ?? null;
}

/**
 * Hit-test a click against the planets in the current system view. Returns
 * the closest in-threshold planet id or null.
 */
export function hitTestSystemViewPlanets(
  layout: SystemViewLayout,
  clickX: number,
  clickY: number,
  thresholdPx = 6,
): PlanetId | null {
  let best: { id: PlanetId; dist: number } | null = null;
  for (const p of layout.planets) {
    const d = Math.hypot(p.x - clickX, p.y - clickY);
    // Threshold scales with planet radius so larger planets get a bigger
    // target.
    if (d <= p.r + thresholdPx) {
      if (!best || d < best.dist) {
        best = { id: p.planet.id, dist: d };
      }
    }
  }
  return best?.id ?? null;
}

export function drawSystemView(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  ui: UiState,
  vp: Viewport,
  vfx: VfxState,
): void {
  const layout = layoutSystemView(state, ui, vp);
  if (!layout) return;

  ctx.save();
  ctx.beginPath();
  ctx.rect(vp.padLeft, vp.padTop, vp.viewW, vp.viewH);
  ctx.clip();

  const {
    cx,
    cy,
    starRadius,
    systemName,
    planets,
    jumpPoints,
    ships,
    boundaryRadiusScreen,
  } = layout;

  // Deep-space backdrop tint — slightly cooler than the galaxy backdrop so
  // the shift into tactical view reads as a "zoom in".
  ctx.fillStyle = 'rgba(6, 10, 22, 0.55)';
  ctx.fillRect(vp.padLeft, vp.padTop, vp.viewW, vp.viewH);

  // M14 + MFix4.2 environment entities (sim-state — asteroid fields,
  // gas giants, nebulae, black holes, ion clouds, radiation belts).
  // Real entities cover the terrain layer that the v2M7 cosmetic
  // placeholders previously simulated; the placeholder pass was retired
  // in MFix4.2 to avoid double-rendering nebulae.
  if (ui.focusedSystemId !== null) {
    drawEnvironmentEntities(ctx, state, ui.focusedSystemId, ui, squareViewport(vp));
  }

  // Outer system boundary — the r=0.48 ring where jump points live.
  ctx.strokeStyle = 'rgba(120, 160, 230, 0.18)';
  ctx.setLineDash([4, 6]);
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(cx, cy, boundaryRadiusScreen, 0, Math.PI * 2);
  ctx.stroke();
  ctx.setLineDash([]);

  // Orbit rings based on each planet's distance from the star.
  ctx.strokeStyle = 'rgba(120, 150, 200, 0.12)';
  ctx.lineWidth = 1;
  for (const p of planets) {
    const r = Math.hypot(p.x - cx, p.y - cy);
    if (r < 4) continue; // planets at the centre (unlikely) would draw a dot
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.stroke();
  }

  // Star glow.
  const glow = ctx.createRadialGradient(cx, cy, starRadius * 0.5, cx, cy, starRadius * 4);
  glow.addColorStop(0, 'rgba(255, 215, 140, 0.5)');
  glow.addColorStop(1, 'rgba(255, 215, 140, 0)');
  ctx.fillStyle = glow;
  ctx.beginPath();
  ctx.arc(cx, cy, starRadius * 4, 0, Math.PI * 2);
  ctx.fill();

  // Star body — bright white-gold core with amber outer.
  const core = ctx.createRadialGradient(cx, cy, 0, cx, cy, starRadius);
  core.addColorStop(0, '#fff8e0');
  core.addColorStop(0.6, '#ffd27a');
  core.addColorStop(1, '#c88a3a');
  ctx.fillStyle = core;
  ctx.beginPath();
  ctx.arc(cx, cy, starRadius, 0, Math.PI * 2);
  ctx.fill();

  // System name anchored top-centre of the viewport.
  ctx.fillStyle = '#d8e4ff';
  ctx.font = 'bold 16px inherit';
  ctx.textAlign = 'center';
  ctx.fillText(systemName, vp.padLeft + vp.viewW / 2, vp.padTop + 24);
  ctx.textAlign = 'left';

  const selectedPlanetId = ui.selection.kind === 'planet' ? ui.selection.id : null;

  // Planets.
  for (const p of planets) {
    const { planet, x, y, r } = p;

    // Ownership halo — empire.color so the palette stays consistent with the
    // galaxy view and the chevron colour of that empire's ships.
    if (planet.ownerEmpireId !== null) {
      const empire = state.empires.get(planet.ownerEmpireId);
      const haloColor = empire?.color ?? '#ff8f8f';
      ctx.strokeStyle = haloColor;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(x, y, r + 4, 0, Math.PI * 2);
      ctx.stroke();
    }

    // Selection ring (draws outside the halo so both are visible).
    if (planet.id === selectedPlanetId) {
      ctx.strokeStyle = '#8fffb0';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(x, y, r + 8, 0, Math.PI * 2);
      ctx.stroke();
    }

    // Planet body — v2M7.7a: radial gradient from biome inner (centre)
    // to biome outer (edge) per GRAPHICS §5.3. v2M7.7b/c overlays the
    // procedural per-biome texture at 40 % alpha clipped to the disc
    // (§10.2 procedural-not-AI path; same blit pattern v3M11 polish
    // will use when commissioned PNGs land).
    const grad = biomeGradient(planet.biome);
    const fill = ctx.createRadialGradient(x, y, 0, x, y, r);
    fill.addColorStop(0, grad.inner);
    fill.addColorStop(1, grad.outer);
    ctx.fillStyle = fill;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
    const texture = getBiomeTexture(planet.biome);
    if (texture) {
      ctx.save();
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.clip();
      ctx.globalAlpha = 0.4;
      ctx.drawImage(texture, x - r, y - r, r * 2, r * 2);
      ctx.restore();
    }

    // v2M7.9a: on-planet structure icon ring per GRAPHICS §6.1.
    // Line-glyph icons sit on a concentric arc 1.2 × planet radius
    // out from disc centre, evenly spread across the upper hemisphere.
    // Under-construction structures render at 40 % alpha + a partial
    // green completion arc derived from queuedAt vs current tick.
    drawStructureIconRing(ctx, state, planet, x, y, r);

    // Planet label — small, offset below the body so it doesn't collide with
    // the halo ring.
    ctx.fillStyle = '#9fb3d9';
    ctx.font = '11px inherit';
    ctx.textAlign = 'center';
    ctx.fillText(planet.name, x, y + r + 14);
    ctx.textAlign = 'left';
  }

  // Jump points — small diamond markers on the boundary ring. Linked JPs get
  // a brighter tint; unlinked ones (v1 default) use a muted tone. Linked JPs
  // also display their destination system's name so the player can pick a
  // jump target without flipping back to the galaxy view.
  for (const j of jumpPoints) {
    const linked = j.jp.linkedSystemId !== null;
    const fill = linked ? '#8fb9ff' : '#4f6ea0';
    const stroke = linked ? '#cfe0ff' : '#7f9ccc';
    ctx.save();
    ctx.translate(j.x, j.y);
    ctx.rotate(Math.PI / 4);
    ctx.fillStyle = fill;
    ctx.strokeStyle = stroke;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.rect(-j.r, -j.r, j.r * 2, j.r * 2);
    ctx.fill();
    ctx.stroke();
    ctx.restore();

    if (linked) {
      const destSys = state.systems.get(j.jp.linkedSystemId!);
      if (destSys) {
        // Offset the label outward (radially away from the star) so it
        // doesn't crowd the diamond or the boundary ring.
        const dx = j.x - cx;
        const dy = j.y - cy;
        const len = Math.hypot(dx, dy) || 1;
        const lx = j.x + (dx / len) * (j.r + 8);
        const ly = j.y + (dy / len) * (j.r + 8);
        ctx.fillStyle = '#9fb3d9';
        ctx.font = '10px inherit';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(`\u2192 ${destSys.name}`, lx, ly);
        ctx.textAlign = 'left';
        ctx.textBaseline = 'alphabetic';
      }
    }
  }

  // Fog-of-war mask (M8-D). Punches scan-range and vision-range holes in a
  // dark overlay so the player can see at a glance where their sensor
  // footprint reaches this tick. Drawn AFTER static sim content (star,
  // planets, jump points) so those get dimmed in unseen areas, and BEFORE
  // move-order previews + ships so player orders + filtered contacts sit
  // bright on top.
  drawFogMask(ctx, state, ui, vp, layout.squareVp);

  // Detection rings for selected player ships (M8-E). Sit over the fog but
  // under move-order previews and chevrons so the rings read as a planning
  // overlay — "this is what each selected ship reaches" — without occluding
  // the ship itself. Empire colour ties each ring to its owner.
  drawSelectionDetectionRings(ctx, state, ui, layout.squareVp);

  // Move-order preview lines — drawn beneath the ships so the chevron stays
  // legible. Only player ships with an active moveOrder render a preview.
  for (const s of ships) {
    if (!s.targetScreen) continue;
    const empire = state.empires.get(s.ship.ownerEmpireId);
    if (!empire?.isPlayer) continue;
    ctx.strokeStyle = 'rgba(143, 255, 176, 0.55)';
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 3]);
    ctx.beginPath();
    ctx.moveTo(s.x, s.y);
    ctx.lineTo(s.targetScreen.x, s.targetScreen.y);
    ctx.stroke();
    ctx.setLineDash([]);
    // Small crosshair at the destination so the player can see where the
    // ship is heading even when it's nearly there.
    ctx.strokeStyle = 'rgba(143, 255, 176, 0.7)';
    ctx.beginPath();
    ctx.arc(s.targetScreen.x, s.targetScreen.y, 4, 0, Math.PI * 2);
    ctx.stroke();
  }

  // Ships — draw with fog-of-war filtering (M8-D):
  //  - Viewer-empire ships always render as chevrons at their live position.
  //  - Other empires' ships render only when the viewer empire's teamVision
  //    row reports seenVia === 'vision' — then we draw the chevron at the
  //    *actual* current position, because vision means we truly see them now.
  //  - Scan contacts and grace ghosts render as dots at the record's
  //    last-seen position via the ghost pass below, NOT the live position.
  //  - No record → ship is culled entirely (even if it's physically here).
  // The viewer empire is the player's empire under normal play and the first
  // non-player empire when the F10 enemy-POV overlay is on (M8.1-F) — so the
  // same filter serves both cases.
  const viewerId = viewerEmpireId(state, ui);
  const viewerVision = state.teamVision.get(viewerId);
  for (const s of ships) {
    const { ship, x, y } = s;
    // v3M13 — devRevealAll bypasses the FoW gate entirely so AI ships
    // render at their live position regardless of teamVision.
    if (!ui.devRevealAll && ship.ownerEmpireId !== viewerId) {
      const rec = viewerVision?.get(ship.id);
      if (!rec || rec.seenVia !== 'vision') continue;
    }
    // v2M7.6a: while a JumpInVfx is animating for this ship, the VFX
    // layer owns the silhouette (bright-dot → line → resolving body
    // over 6 ticks). Suppress the regular draw so the two layers don't
    // fight for the same screen space.
    if (isShipInJumpIn(vfx, ship.id, state.tick)) continue;
    const empire = state.empires.get(ship.ownerEmpireId);
    const accent = empire?.color ?? '#ff8f8f';
    const selected = ui.selectedShipIds.has(ship.id);

    // MFix10.3 — Minefield is conceptually a hazard zone, not a ship.
    // Render as a translucent danger-zone disc with the catalogue
    // radius (`features.minefield.radius`) instead of the chevron
    // path. Hull% drives opacity so depleted fields fade out.
    if (ship.chassis === MINEFIELD_CHASSIS) {
      const tmpl = empire
        ? state.shipTemplates.get(`${empire.raceId}:${ship.chassis}`)
        : undefined;
      const hullPct = tmpl
        ? Math.max(0, Math.min(1, ship.currentHull / tmpl.hull))
        : 1;
      const worldR = minefieldRadius(state.structuresData);
      const screenR = worldR * ui.systemCamera.zoom * layout.squareVp.viewW;
      ctx.save();
      ctx.globalAlpha = 0.18 + 0.22 * hullPct;
      ctx.fillStyle = '#d24548';
      tracePerturbedCircle(ctx, x, y, screenR, ship.id, 0.08);
      ctx.fill();
      ctx.globalAlpha = 0.45 + 0.35 * hullPct;
      ctx.strokeStyle = selected ? accent : '#f0a93b';
      ctx.lineWidth = selected ? 2 : 1;
      ctx.setLineDash([4, 3]);
      tracePerturbedCircle(ctx, x, y, screenR, ship.id, 0.08);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.restore();
      continue;
    }
    // Cloak indicator is owner-scoped: other empires' cloaked ships are
    // culled entirely by fog-of-war above, so the cloakPhase here is
    // populated only for the viewer's own cloaked units. Hands the visual
    // "your own unit is hidden" read to the §4.1 contract via cloakPhase.
    const ownCloaked = ship.cloaked && ship.ownerEmpireId === viewerId;
    const tmpl = empire
      ? state.shipTemplates.get(`${empire.raceId}:${ship.chassis}`)
      : undefined;
    // v2M7.2.5: derive a placeholder dimensions tuple from classLevel
    // until Ships.xlsx ships Length / Width columns. drawShip uses
    // classLevel directly via classScale; dimensions are exported on
    // the input for future v3 readers.
    const classLevel = tmpl?.classLevel ?? 1;
    const baseLength = 12;
    const baseWidth = 8;
    // v2M7.3: heading derived from active moveOrder. Stationary ships
    // default to -π/2 (pointing up = north) so the canonical silhouette
    // orientation reads correctly. Heading uses world-frame math
    // convention: 0 = +x axis (right). drawShip applies a +π/2 rotation
    // offset to align canonical-up with that convention.
    let heading = -Math.PI / 2;
    if (ship.moveOrder !== null) {
      const dx = ship.moveOrder.targetX - ship.sx;
      const dy = ship.moveOrder.targetY - ship.sy;
      if (dx !== 0 || dy !== 0) {
        heading = Math.atan2(dy, dx);
      }
    }
    const input: ShipRenderInput = {
      chassisId: ship.chassis,
      race: empire?.raceId ?? 'RACE-AA',
      x,
      y,
      heading,
      hullPct: tmpl ? Math.max(0, Math.min(1, ship.currentHull / tmpl.hull)) : 1,
      shieldPct:
        tmpl && tmpl.shields > 0
          ? Math.max(0, Math.min(1, ship.currentShields / tmpl.shields))
          : 0,
      cloakPhase: ownCloaked ? 0.5 : null,
      selected,
      dimensions: { length: baseLength, width: baseWidth },
      classLevel,
    };
    drawShip(ctx, input, accent);
  }

  // Ghost / scan-contact pass — dots at lastSeenSx/Sy for every teamVision
  // record anchored to this system whose channel is 'scan' or null (grace).
  // Vision contacts were drawn as live chevrons above. Solid-ish colour for
  // scan, faded for ghost so the viewer can tell "active lock" from
  // "coasting memory" at a glance. Owner colour comes from the ship's live
  // empire row so a switch of sides would flip the dot; harmless in v1.
  // v3M13 — devRevealAll renders every ship live above, so ghost/scan
  // dots add no information; suppress them under reveal-all.
  if (!ui.devRevealAll && viewerVision && ui.focusedSystemId !== null) {
    for (const rec of viewerVision.values()) {
      if (rec.lastSeenSystemId !== ui.focusedSystemId) continue;
      if (rec.seenVia === 'vision') continue;
      const ship = state.ships.get(rec.shipId);
      const empire = ship ? state.empires.get(ship.ownerEmpireId) : undefined;
      const color = empire?.color ?? '#ff8f8f';
      const p = worldToScreen(ui.systemCamera, layout.squareVp, {
        x: rec.lastSeenSx,
        y: rec.lastSeenSy,
      });
      ctx.save();
      ctx.globalAlpha = rec.seenVia === 'scan' ? 0.9 : 0.45;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(p.x, p.y, 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.restore();
    }
  }

  // v2M7.9b — Repair Yard aura overlay: pulsing Shield-blue ring inside
  // the docking-bay cut-out, alpha 0.2 → 0.5 over 1 s (10 ticks). Drawn
  // after the ship pass so it sits visually inside the silhouette.
  for (const s of layout.ships) {
    if (!/RepairYard/i.test(s.ship.chassis)) continue;
    if (s.ship.ownerEmpireId !== viewerId) {
      const rec = viewerVision?.get(s.ship.id);
      if (!rec || rec.seenVia !== 'vision') continue;
    }
    // 10-tick (1 s @ 10 Hz) triangle wave: 0..1..0..1...
    const phase = (state.tick % 10) / 10;
    const tri = phase < 0.5 ? phase * 2 : (1 - phase) * 2;
    const alpha = 0.2 + 0.3 * tri;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.fillStyle = '#4FB3FF'; // Shield-blue per GRAPHICS §2.1
    ctx.beginPath();
    ctx.arc(s.x, s.y, 3, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  // v2M7.9c — Constructor scaffold cage: 1 s fade-in (10 ticks) over a
  // ship that has parked at its build site (constructionOrder set,
  // moveOrder cleared per advance-construction.ts). Cage size scales
  // with the structure-kind template's class so a freighter-class
  // scaffold reads larger than a buoy-class one. Position is the
  // captured order.sx/sy (Constructor stays on site).
  for (const ship of state.ships.values()) {
    const order = ship.constructionOrder;
    if (order === null) continue;
    if (ship.moveOrder !== null) continue;
    if (ship.systemId === null) continue;
    if (ship.systemId !== ui.focusedSystemId) continue;
    if (ship.ownerEmpireId !== viewerId) {
      const rec = viewerVision?.get(ship.id);
      if (!rec || rec.seenVia !== 'vision') continue;
    }
    const elapsed = order.ticksTotal - order.ticksRemaining;
    const fade = Math.min(1, elapsed / 10);
    if (fade <= 0) continue;
    const empire = state.empires.get(ship.ownerEmpireId);
    const accent = empire?.color ?? '#ff8f8f';
    const screen = worldToScreen(ui.systemCamera, layout.squareVp, {
      x: order.sx,
      y: order.sy,
    });
    drawScaffoldCage(ctx, screen.x, screen.y, fade, accent);
  }

  // v3M22.4 — Waypoint markers for selected player-owned ships with
  // non-empty waypoints. Drawn after sim-state ship passes so the
  // numbered dots + dashed line sit visually on top of the ship icon
  // and any ghost/scan contacts. Only renders for ships the viewer
  // owns AND has selected, so the visual is local to the player's
  // active focus.
  if (ui.selectedShipIds.size > 0) {
    for (const id of ui.selectedShipIds) {
      const ship = state.ships.get(id);
      if (!ship) continue;
      if (ship.ownerEmpireId !== viewerId) continue;
      if (ship.systemId !== ui.focusedSystemId) continue;
      if (ship.waypoints.length === 0) continue;
      const empire = state.empires.get(ship.ownerEmpireId);
      const accent = empire?.color ?? '#ffe28a';
      drawWaypointMarkers(ctx, ship, accent, (wx, wy) =>
        worldToScreen(ui.systemCamera, layout.squareVp, { x: wx, y: wy }),
      );
    }
  }

  // v3M22.4 — Guard-target picker rings. Pulses on every same-system
  // planet (any owner) and player-owned ship (excluding the guarding
  // ship itself), giving visual targets for the player to LMB.
  if (
    ui.guardTargetMode !== null &&
    ui.guardTargetMode.systemId === ui.focusedSystemId
  ) {
    drawGuardTargetRings(ctx, state, ui, layout);
  }

  // v2M7.4a: weapon-tracer VFX. Drawn after the ship/ghost passes so
  // tracers sit visually "in front" of the units that fired them, but
  // before the dev overlay + marquee so debug surfaces still read on top.
  // Camera transform is the same (camera, squareVp) used for ship
  // chevrons — passed through as a closure so vfx.ts stays decoupled
  // from camera types.
  drawVfx(
    ctx,
    vfx,
    state,
    viewerId,
    (wx, wy) => worldToScreen(ui.systemCamera, layout.squareVp, { x: wx, y: wy }),
    ui.devRevealAll,
    ui.focusedSystemId,
  );

  // Dev detection overlay (M8-F) — F9-toggled. Draws every ship's vision +
  // scan rings (all teams) and effective det/stealth labels so balance
  // iteration doesn't require debugger inspection. Rendered last (except
  // marquee) so it sits on top of every sim element.
  if (ui.devDetectionOverlay) {
    drawDevDetectionOverlay(ctx, state, ui, layout.squareVp);
  }

  // Active drag-to-select marquee (tactical view only; UiState guarantees
  // ui.marquee is null outside system view but we guard anyway).
  if (ui.marquee) {
    const m = marqueeBounds(ui.marquee);
    ctx.save();
    ctx.strokeStyle = 'rgba(143, 255, 176, 0.9)';
    ctx.fillStyle = 'rgba(143, 255, 176, 0.12)';
    ctx.lineWidth = 1;
    ctx.setLineDash([3, 3]);
    ctx.beginPath();
    ctx.rect(m.minX, m.minY, m.maxX - m.minX, m.maxY - m.minY);
    ctx.fill();
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();
  }

  // M14 build-placement ghost — drawn last so it sits on top of everything
  // in the tactical view. Green if the cursor's world position is a valid
  // site for the queued structure kind, red if not.
  if (ui.buildPlacement !== null && ui.cursorWorld !== null) {
    drawBuildPlacementGhost(ctx, state, ui, squareViewport(vp));
  }

  ctx.restore();
}

/**
 * Paint a fog-of-war mask over the viewport (M8-D). The mask is built on an
 * offscreen canvas using destination-out compositing so the two reveal
 * thresholds (scan = partial reveal, vision = full reveal) compose cleanly
 * without touching the pixels beneath. It's then blitted onto the main
 * canvas at 0.75 overall alpha — so the "dark" regions don't fully occlude
 * the environmental/planet layer; they just dim it.
 *
 * Three visual zones (darkest -> brightest):
 *   - Outside every scan disc     -> full fog
 *   - Inside scan, outside vision -> partial fog (mid-grey)
 *   - Inside any vision disc      -> no fog
 *
 * Scanners are collected via collectTeamScannersForSystem, so the v2
 * cross-system / structure-scanner extension plugs in via that helper
 * without touching this file. Vision/scan radii are scaled by
 * visionRangeScale / scanRangeScale exactly as the sim-side helpers do
 * (detection.ts) - keeping the mask in lock-step with actual probabilities.
 */
function drawFogMask(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  ui: UiState,
  vp: Viewport,
  squareVp: Viewport,
): void {
  if (ui.focusedSystemId === null) return;
  // Fog reflects the viewer empire's scanners — player under normal play,
  // first non-player empire when F10 enemy-POV overlay is on (M8.1-F).
  const viewerId = viewerEmpireId(state, ui);
  const scanners = collectTeamScannersForSystem(
    state,
    viewerId,
    ui.focusedSystemId,
    DEFAULT_DETECTION_BALANCE,
  );
  if (scanners.length === 0) {
    // No scanners in this system - blanket the viewport in full fog. Still
    // use the same blit alpha so the feel is continuous between "no scanner"
    // and "scanner but you're way outside its ring".
    ctx.save();
    ctx.fillStyle = 'rgba(5, 8, 15, 0.75)';
    ctx.fillRect(vp.padLeft, vp.padTop, vp.viewW, vp.viewH);
    ctx.restore();
    return;
  }

  const w = Math.max(1, Math.floor(vp.viewW));
  const h = Math.max(1, Math.floor(vp.viewH));
  const off = document.createElement('canvas');
  off.width = w;
  off.height = h;
  const fogCtx = off.getContext('2d');
  if (!fogCtx) return;

  // Solid fog on the offscreen canvas. Opaque so the destination-out math
  // below reveals cleanly - the final blit alpha (0.75) shapes the overall
  // darkness.
  fogCtx.fillStyle = '#05080f';
  fogCtx.fillRect(0, 0, w, h);

  fogCtx.globalCompositeOperation = 'destination-out';

  // Scan discs first at alpha 0.6 - removes 60% of the fog alpha, leaving a
  // mid-grey band wherever only scan (not vision) reaches.
  fogCtx.globalAlpha = 0.6;
  for (const scanner of scanners) {
    const r01 = scanner.scanRange * DEFAULT_DETECTION_BALANCE.scanRangeScale;
    if (!(r01 > 0)) continue;
    const c = worldToScreen(ui.systemCamera, squareVp, {
      x: scanner.sx,
      y: scanner.sy,
    });
    const rPx = r01 * ui.systemCamera.zoom * squareVp.viewW;
    fogCtx.beginPath();
    fogCtx.arc(c.x - vp.padLeft, c.y - vp.padTop, rPx, 0, Math.PI * 2);
    fogCtx.fill();
  }

  // Vision discs on top at alpha 1.0 - fully clears the fog inside vision
  // range. Stacks over the partial scan reveal without caring about overlap
  // order (destination-out is idempotent on already-cleared pixels).
  fogCtx.globalAlpha = 1.0;
  for (const scanner of scanners) {
    const r01 = scanner.visionRange * DEFAULT_DETECTION_BALANCE.visionRangeScale;
    if (!(r01 > 0)) continue;
    const c = worldToScreen(ui.systemCamera, squareVp, {
      x: scanner.sx,
      y: scanner.sy,
    });
    const rPx = r01 * ui.systemCamera.zoom * squareVp.viewW;
    fogCtx.beginPath();
    fogCtx.arc(c.x - vp.padLeft, c.y - vp.padTop, rPx, 0, Math.PI * 2);
    fogCtx.fill();
  }

  // Blit the composed fog onto the main canvas. Alpha 0.75 so underlying
  // sim content (planets, jump points, star glow) dims rather than
  // disappears - players should still sense the system's shape under fog.
  ctx.save();
  ctx.globalAlpha = 0.75;
  ctx.drawImage(off, vp.padLeft, vp.padTop);
  ctx.restore();
}

/**
 * Draw vision + scan rings centred on every currently-selected player ship
 * in the focused system (M8-E). Enemies can't be selected in v1 so the filter
 * is tight: own-empire + in this system + template resolvable. The radii
 * mirror detection.ts exactly (visionRange/scanRange * their range scale)
 * so a player eyeballing a ring against the fog mask sees the two in
 * lock-step.
 *
 * Scan ring is drawn dashed at low alpha so it reads as "possible"
 * coverage, vision ring is solid at higher alpha so it reads as "certain"
 * coverage. Both stay under the chevron z-order so they don't obscure the
 * ship itself.
 */
function drawSelectionDetectionRings(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  ui: UiState,
  squareVp: Viewport,
): void {
  if (ui.focusedSystemId === null) return;
  if (ui.selectedShipIds.size === 0) return;

  // Gate on the VIEWER empire, not the player empire — otherwise F10 enemy-POV
  // leaks the player's selected-ship detection footprint into the enemy's view
  // (which is the exact thing the POV toggle exists to verify against).
  const viewerId = viewerEmpireId(state, ui);
  for (const shipId of ui.selectedShipIds) {
    const ship = state.ships.get(shipId);
    if (!ship) continue;
    if (ship.systemId !== ui.focusedSystemId) continue;
    if (ship.ownerEmpireId !== viewerId) continue;
    const empire = state.empires.get(ship.ownerEmpireId);
    if (!empire) continue;
    const tmpl = state.shipTemplates.get(`${empire.raceId}:${ship.chassis}`);
    if (!tmpl) continue;

    const centre = worldToScreen(ui.systemCamera, squareVp, {
      x: ship.sx,
      y: ship.sy,
    });
    const color = empire.color;
    const zoomFactor = ui.systemCamera.zoom * squareVp.viewW;

    // Scan ring — outer, dashed, low alpha. Drawn first so the vision ring
    // on top reads as the "tighter, surer" coverage.
    const scanR01 =
      tmpl.scanRange * DEFAULT_DETECTION_BALANCE.scanRangeScale;
    if (scanR01 > 0) {
      const rPx = scanR01 * zoomFactor;
      ctx.save();
      ctx.globalAlpha = 0.4;
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.arc(centre.x, centre.y, rPx, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }

    // Vision ring - inner, solid, higher alpha.
    const visR01 =
      tmpl.visionRange * DEFAULT_DETECTION_BALANCE.visionRangeScale;
    if (visR01 > 0) {
      const rPx = visR01 * zoomFactor;
      ctx.save();
      ctx.globalAlpha = 0.7;
      ctx.strokeStyle = color;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(centre.x, centre.y, rPx, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }
  }
}

/**
 * Dev-only detection overlay (M8-F). When UiState.devDetectionOverlay is
 * true, draws every in-system ship's vision + scan rings in that empire's
 * colour plus a small "det/stealth" label under each chevron. Cloaked ships
 * get a "CLOAK" tag so the effective-stealth=irrelevant case is visually
 * distinct from a genuinely high-stealth hull.
 *
 * Pure reader — no RNG, no state mutation. The effective values use the
 * same helpers (`effectiveStealth`, `effectiveDetection`) the sim pass uses,
 * so what the overlay shows IS what the detection pass is computing.
 *
 * Deliberately NOT hooked to the fog mask: the overlay reveals everything,
 * regardless of who's looking. It's an omniscient debug view, not a
 * player-facing UI — F9 is the on/off switch.
 */
function drawDevDetectionOverlay(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  ui: UiState,
  squareVp: Viewport,
): void {
  if (ui.focusedSystemId === null) return;
  const zoomFactor = ui.systemCamera.zoom * squareVp.viewW;

  for (const ship of state.ships.values()) {
    if (ship.systemId !== ui.focusedSystemId) continue;
    const empire = state.empires.get(ship.ownerEmpireId);
    if (!empire) continue;
    const tmpl = state.shipTemplates.get(`${empire.raceId}:${ship.chassis}`);
    if (!tmpl) continue;

    const centre = worldToScreen(ui.systemCamera, squareVp, {
      x: ship.sx,
      y: ship.sy,
    });
    const color = empire.color;

    // Scan ring — outer, dashed.
    const scanR01 = tmpl.scanRange * DEFAULT_DETECTION_BALANCE.scanRangeScale;
    if (scanR01 > 0) {
      const rPx = scanR01 * zoomFactor;
      ctx.save();
      ctx.globalAlpha = 0.35;
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 5]);
      ctx.beginPath();
      ctx.arc(centre.x, centre.y, rPx, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }

    // Vision ring - inner, solid.
    const visR01 = tmpl.visionRange * DEFAULT_DETECTION_BALANCE.visionRangeScale;
    if (visR01 > 0) {
      const rPx = visR01 * zoomFactor;
      ctx.save();
      ctx.globalAlpha = 0.55;
      ctx.strokeStyle = color;
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.arc(centre.x, centre.y, rPx, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }

    // Label - effective det/stealth pulled through the same helpers the
    // sim uses, so firing-breaks-stealth and any future modifiers show up
    // here for free.
    const detEff = effectiveDetection(tmpl.detectionBase, DEFAULT_DETECTION_BALANCE);
    const stEff = effectiveStealth(
      tmpl.stealthBase,
      ship.lastFiredTick,
      state.tick,
      DEFAULT_DETECTION_BALANCE,
    );
    const label = ship.cloaked
      ? `${detEff}/${stEff} CLOAK`
      : `${detEff}/${stEff}`;
    ctx.save();
    ctx.fillStyle = color;
    ctx.globalAlpha = 0.9;
    ctx.font = '10px inherit';
    ctx.textAlign = 'center';
    ctx.fillText(label, centre.x, centre.y + SHIP_SCREEN_RADIUS + 14);
    ctx.textAlign = 'left';
    ctx.restore();
  }
}

/**
 * Draw M14 environment entities (asteroid fields + gas giants) in the
 * focused system. Filled translucent circles so the player can see where
 * to place extractor Constructors. Distinct visually from the cosmetic
 * nebula placeholders above.
 */
/**
 * MFix6.5 — Per-kind shape-deformation amplitude. 0 keeps the shape a
 * perfect circle (black-hole singularity stays round); 0.4-0.55 gives
 * the soft-cloud / asteroid kinds a properly organic, non-uniform
 * footprint. `gas-giant` deforms only mildly so the radial-gradient
 * atmosphere still reads as a planet rather than a blob.
 */
const ENV_DEFORM_AMPLITUDE: Readonly<Record<EnvironmentEntity['kind'], number>> =
  Object.freeze({
    'asteroid-field': 0.4,
    'gas-giant': 0.15,
    nebula: 0.5,
    'ion-cloud': 0.55,
    'radiation-belt': 0.3,
    'black-hole': 0,
  });

/** Polygon vertex count for the deformed-circle trace. 16 is enough to read as smooth at the env entity sizes used in v1. */
const ENV_DEFORM_VERTICES = 16;

/**
 * MFix6.5 — Trace a polygon path approximating a circle of radius
 * `baseR` around `(cx, cy)`, with each vertex's distance perturbed by a
 * deterministic seeded pseudo-noise function. Caller calls fill/stroke
 * after. When `amplitude` is 0, falls back to a true `ctx.arc` so
 * shapes meant to read as round (black-hole singularity) stay round.
 *
 * Determinism: the seed (typically the env entity id) drives the noise
 * across the vertices, so the same entity always renders the same
 * shape across frames + camera positions. Shape coherence: when
 * multiple radii share the same seed (concentric rings of a single
 * env entity), they all deform with the same vertex pattern, reading
 * as nested same-shape rings rather than independent jaggies.
 */
function tracePerturbedCircle(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  baseR: number,
  seed: number,
  amplitude: number,
  vertices: number = ENV_DEFORM_VERTICES,
): void {
  if (amplitude <= 0 || baseR <= 0) {
    ctx.beginPath();
    ctx.arc(cx, cy, baseR, 0, Math.PI * 2);
    return;
  }
  ctx.beginPath();
  for (let i = 0; i < vertices; i++) {
    const angle = (i / vertices) * Math.PI * 2;
    const noise = hashNoise(seed, i);
    const r = baseR * (1 + noise * amplitude);
    const x = cx + Math.cos(angle) * r;
    const y = cy + Math.sin(angle) * r;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
}

/**
 * Hash-based pseudo-random in [-1, 1]. Mulberry32-flavoured mix;
 * deterministic on (seed, i) inputs. Good enough for visual
 * deformation — not cryptographic, not statistically uniform, but
 * stable across frames and uncorrelated enough between adjacent
 * indices that the polygon doesn't read as smooth.
 */
function hashNoise(seed: number, i: number): number {
  let h = Math.imul(seed | 0, 2654435761) ^ Math.imul(i | 0, 1779033703);
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  h ^= h >>> 16;
  return ((h >>> 0) / 0xffffffff) * 2 - 1;
}

function drawEnvironmentEntities(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  focusedSystemId: SystemId,
  ui: UiState,
  squareVp: Viewport,
): void {
  for (const env of state.environment.values()) {
    if (env.systemId !== focusedSystemId) continue;
    const centre = worldToScreen(ui.systemCamera, squareVp, {
      x: env.sx,
      y: env.sy,
    });
    const r = env.radius * ui.systemCamera.zoom * squareVp.viewW;
    // MFix6.5 — Per-kind deformation. The seed is the entity id so
    // every render of the same entity produces the same shape across
    // ticks + camera moves.
    const amp = ENV_DEFORM_AMPLITUDE[env.kind];
    const seed = env.id;
    ctx.save();
    if (env.kind === 'asteroid-field') {
      // Brown-tinted speckle suggesting rock density. Filled translucent
      // region for the "this is where mining works" footprint.
      ctx.fillStyle = 'rgba(150, 110, 80, 0.15)';
      ctx.strokeStyle = 'rgba(180, 130, 90, 0.45)';
      tracePerturbedCircle(ctx, centre.x, centre.y, r, seed, amp);
      ctx.fill();
      ctx.setLineDash([2, 3]);
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.setLineDash([]);
    } else if (env.kind === 'gas-giant') {
      // Warm swirl — gradient suggests atmosphere.
      const g = ctx.createRadialGradient(centre.x, centre.y, 0, centre.x, centre.y, r);
      g.addColorStop(0, 'rgba(220, 170, 90, 0.35)');
      g.addColorStop(1, 'rgba(220, 170, 90, 0)');
      ctx.fillStyle = g;
      tracePerturbedCircle(ctx, centre.x, centre.y, r, seed, amp);
      ctx.fill();
      ctx.strokeStyle = 'rgba(230, 180, 110, 0.35)';
      ctx.lineWidth = 1;
      ctx.stroke();
    } else if (env.kind === 'nebula') {
      // MFix4.2 — soft cyan-magenta gradient, suggests gas cloud.
      const g = ctx.createRadialGradient(centre.x, centre.y, 0, centre.x, centre.y, r);
      g.addColorStop(0, 'rgba(160, 110, 200, 0.30)');
      g.addColorStop(0.6, 'rgba(80, 140, 200, 0.18)');
      g.addColorStop(1, 'rgba(80, 140, 200, 0)');
      ctx.fillStyle = g;
      tracePerturbedCircle(ctx, centre.x, centre.y, r, seed, amp);
      ctx.fill();
    } else if (env.kind === 'black-hole') {
      // MFix4.2 — dense black core with a thin accretion-disc ring.
      // Amplitude 0 → kept perfectly round. The singularity should
      // read as a hole, not a blob.
      ctx.fillStyle = 'rgba(0, 0, 0, 0.95)';
      tracePerturbedCircle(ctx, centre.x, centre.y, r * 0.4, seed, amp);
      ctx.fill();
      ctx.strokeStyle = 'rgba(255, 200, 120, 0.6)';
      ctx.lineWidth = 2;
      tracePerturbedCircle(ctx, centre.x, centre.y, r * 0.55, seed, amp);
      ctx.stroke();
      ctx.strokeStyle = 'rgba(255, 200, 120, 0.25)';
      ctx.lineWidth = 1;
      tracePerturbedCircle(ctx, centre.x, centre.y, r * 0.85, seed, amp);
      ctx.stroke();
    } else if (env.kind === 'ion-cloud') {
      // MFix4.2 — pulsing electric-blue ring, suggests ionised plasma.
      ctx.strokeStyle = 'rgba(120, 200, 255, 0.55)';
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 3]);
      tracePerturbedCircle(ctx, centre.x, centre.y, r * 0.7, seed, amp);
      ctx.stroke();
      ctx.setLineDash([2, 4]);
      tracePerturbedCircle(ctx, centre.x, centre.y, r, seed, amp);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = 'rgba(120, 200, 255, 0.10)';
      tracePerturbedCircle(ctx, centre.x, centre.y, r, seed, amp);
      ctx.fill();
    } else if (env.kind === 'radiation-belt') {
      // MFix4.2 — yellow-green hazard hash, suggests radiation field.
      ctx.fillStyle = 'rgba(180, 220, 80, 0.12)';
      tracePerturbedCircle(ctx, centre.x, centre.y, r, seed, amp);
      ctx.fill();
      ctx.strokeStyle = 'rgba(180, 220, 80, 0.55)';
      ctx.lineWidth = 1;
      ctx.setLineDash([1, 2]);
      tracePerturbedCircle(ctx, centre.x, centre.y, r * 0.5, seed, amp);
      ctx.stroke();
      tracePerturbedCircle(ctx, centre.x, centre.y, r * 0.8, seed, amp);
      ctx.stroke();
      ctx.setLineDash([]);
    }
    ctx.restore();
  }
}

/**
 * Draw the M14 build-placement ghost at the cursor's world position. Colour
 * is green when `isValidBuildSite` returns true for the current kind at
 * the cursor, red otherwise. Also draws a label with the kind name so the
 * player can tell at a glance what they're about to place.
 */
function drawBuildPlacementGhost(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  ui: UiState,
  squareVp: Viewport,
): void {
  if (!ui.buildPlacement || !ui.cursorWorld) return;
  const valid = isValidBuildSite(
    state,
    ui.cursorWorld.systemId,
    ui.buildPlacement.structureKind,
    ui.cursorWorld.sx,
    ui.cursorWorld.sy,
  );
  const colour = valid ? 'rgba(100, 220, 140, 0.9)' : 'rgba(230, 100, 100, 0.9)';
  const fill = valid ? 'rgba(100, 220, 140, 0.18)' : 'rgba(230, 100, 100, 0.18)';
  const centre = worldToScreen(ui.systemCamera, squareVp, {
    x: ui.cursorWorld.sx,
    y: ui.cursorWorld.sy,
  });
  // Footprint circle — generous enough to be visible. Matches the rough
  // footprint of a space-structure ship once built.
  const r = 16;

  ctx.save();
  ctx.fillStyle = fill;
  ctx.strokeStyle = colour;
  ctx.lineWidth = 1.5;
  ctx.setLineDash([4, 4]);
  ctx.beginPath();
  ctx.arc(centre.x, centre.y, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  ctx.setLineDash([]);

  // Crosshair.
  ctx.beginPath();
  ctx.moveTo(centre.x - r - 4, centre.y);
  ctx.lineTo(centre.x + r + 4, centre.y);
  ctx.moveTo(centre.x, centre.y - r - 4);
  ctx.lineTo(centre.x, centre.y + r + 4);
  ctx.stroke();

  // Label.
  ctx.fillStyle = colour;
  ctx.font = '11px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'bottom';
  ctx.fillText(ui.buildPlacement.structureKind, centre.x, centre.y - r - 8);
  ctx.restore();
}

/**
 * v2M7.9a — on-planet structure icon ring per GRAPHICS §6.1.
 *
 * Layout: each of the planet's structures gets a small line-glyph icon
 * placed on a concentric arc 1.2 × planet radius out from disc centre.
 * Slots are distributed across the upper hemisphere from −3π/4 to −π/4
 * (135° span — "above" the planet, rolling left to right).
 *
 * Visual states:
 *   - Active: full opacity, neutral HUD-text-dim stroke.
 *   - Under construction (`buildTicksRemaining > 0`): 40 % alpha glyph
 *     plus a Success-green partial-arc completion ring around it. The
 *     arc's fill fraction is `elapsed / (elapsed + remaining)` where
 *     `elapsed = state.tick - structure.queuedAt` — uses existing
 *     fields without needing a new `buildTicksTotal` on Structure.
 */
function drawStructureIconRing(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  planet: Planet,
  cx: number,
  cy: number,
  planetR: number,
): void {
  const ids = planet.structureIds;
  if (!ids || ids.length === 0) return;
  const ringR = planetR * 1.2;
  const startAngle = -Math.PI * 0.75;
  const endAngle = -Math.PI * 0.25;
  const span = endAngle - startAngle;
  for (let i = 0; i < ids.length; i++) {
    const struct = state.structures.get(ids[i]);
    if (!struct) continue;
    const t = ids.length === 1 ? 0.5 : i / (ids.length - 1);
    const a = startAngle + span * t;
    const ix = cx + Math.cos(a) * ringR;
    const iy = cy + Math.sin(a) * ringR;
    const underConstruction = struct.buildTicksRemaining > 0;
    ctx.save();
    ctx.globalAlpha = underConstruction ? 0.4 : 1.0;
    ctx.strokeStyle = '#9FB3D9';
    ctx.fillStyle = '#9FB3D9';
    ctx.lineWidth = 1;
    ctx.translate(ix, iy);
    drawStructureGlyph(ctx, struct.kind);
    ctx.restore();
    if (underConstruction) {
      const elapsed = state.tick - struct.queuedAt;
      const total = elapsed + struct.buildTicksRemaining;
      const progress = total > 0 ? elapsed / total : 0;
      ctx.save();
      ctx.strokeStyle = '#8fffb0';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(ix, iy, 8, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * progress);
      ctx.stroke();
      ctx.restore();
    }
  }
}

/**
 * Draw a single 14 px structure icon glyph centred at (0, 0). Each
 * glyph uses minimal line-art primitives that read at small sizes
 * (the icon ring sits just outside the planet disc on a tactical-view
 * frame, so glyphs rarely exceed 14 px tall in practice).
 *
 * GRAPHICS §6.1 specifies eight glyphs (Shipyard / Research Lab /
 * Trade Center / Cloning Center / Processing Plant / Refinery /
 * Population Dome / Defence). The v1 StructureKind enum ships fourteen
 * kinds — we extend the spec table to cover the extras (bank, warehouse,
 * fuel-dump, embassy) and split the single 'Defence' entry into four
 * sub-glyphs so the player can read planetary-shields vs underground-
 * bunkers vs energy-battery vs kinetic-battery at a glance.
 */
function drawStructureGlyph(
  ctx: CanvasRenderingContext2D,
  kind: StructureKind,
): void {
  switch (kind) {
    case 'shipyard':
      // T-frame: vertical mast + cross-bar at top.
      ctx.beginPath();
      ctx.moveTo(0, -6);
      ctx.lineTo(0, 6);
      ctx.moveTo(-5, -6);
      ctx.lineTo(5, -6);
      ctx.stroke();
      break;
    case 'research-lab':
      // Beaker: trapezoid body + small dome at top.
      ctx.beginPath();
      ctx.moveTo(-3, -5);
      ctx.lineTo(3, -5);
      ctx.lineTo(5, 5);
      ctx.lineTo(-5, 5);
      ctx.closePath();
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(0, -5, 1.5, 0, Math.PI, true);
      ctx.stroke();
      break;
    case 'trade-center':
      // Stack of three coins.
      for (let i = -2; i <= 2; i += 2) {
        ctx.beginPath();
        ctx.ellipse(0, i, 5, 1.5, 0, 0, Math.PI * 2);
        ctx.stroke();
      }
      break;
    case 'cloning-center':
      // DNA helix — two opposing sine-curve strands.
      ctx.beginPath();
      for (let yy = -6; yy <= 6; yy += 0.8) {
        const xx = 4 * Math.sin((yy / 6) * Math.PI);
        if (yy === -6) ctx.moveTo(xx, yy);
        else ctx.lineTo(xx, yy);
      }
      ctx.stroke();
      ctx.beginPath();
      for (let yy = -6; yy <= 6; yy += 0.8) {
        const xx = -4 * Math.sin((yy / 6) * Math.PI);
        if (yy === -6) ctx.moveTo(xx, yy);
        else ctx.lineTo(xx, yy);
      }
      ctx.stroke();
      break;
    case 'processing-plant':
      // Gear: circle + 6 short radial spokes outside.
      ctx.beginPath();
      ctx.arc(0, 0, 3.5, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      for (let i = 0; i < 6; i++) {
        const ang = (Math.PI / 3) * i;
        ctx.moveTo(Math.cos(ang) * 4, Math.sin(ang) * 4);
        ctx.lineTo(Math.cos(ang) * 6, Math.sin(ang) * 6);
      }
      ctx.stroke();
      break;
    case 'refinery':
      // Smokestack: vertical rectangle + a small triangle plume above.
      ctx.beginPath();
      ctx.rect(-2, -3, 4, 9);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(-2, -3);
      ctx.lineTo(0, -6);
      ctx.lineTo(2, -3);
      ctx.stroke();
      break;
    case 'bank':
      // Stack of three horizontal bars (currency motif).
      ctx.beginPath();
      ctx.rect(-5, -4, 10, 1.5);
      ctx.rect(-5, -1, 10, 1.5);
      ctx.rect(-5, 2, 10, 1.5);
      ctx.stroke();
      break;
    case 'warehouse':
      // Box with an X across the front.
      ctx.beginPath();
      ctx.rect(-5, -4, 10, 8);
      ctx.moveTo(-5, -4);
      ctx.lineTo(5, 4);
      ctx.moveTo(5, -4);
      ctx.lineTo(-5, 4);
      ctx.stroke();
      break;
    case 'fuel-dump':
      // Triangle with a horizontal bar through the middle.
      ctx.beginPath();
      ctx.moveTo(0, -6);
      ctx.lineTo(6, 5);
      ctx.lineTo(-6, 5);
      ctx.closePath();
      ctx.moveTo(-3, 1);
      ctx.lineTo(3, 1);
      ctx.stroke();
      break;
    case 'embassy':
      // Pennant flag — vertical pole + triangle banner.
      ctx.beginPath();
      ctx.moveTo(-3, -6);
      ctx.lineTo(-3, 6);
      ctx.moveTo(-3, -6);
      ctx.lineTo(4, -3);
      ctx.lineTo(-3, 0);
      ctx.stroke();
      break;
    case 'planetary-shields':
      // Shield + vertical accent bar.
      drawShieldOutline(ctx);
      ctx.beginPath();
      ctx.moveTo(0, -3);
      ctx.lineTo(0, 3);
      ctx.stroke();
      break;
    case 'underground-bunkers':
      // Shield + horizontal accent bar.
      drawShieldOutline(ctx);
      ctx.beginPath();
      ctx.moveTo(-3, 0);
      ctx.lineTo(3, 0);
      ctx.stroke();
      break;
    case 'energy-surface-battery':
      // Lightning bolt zigzag.
      ctx.beginPath();
      ctx.moveTo(2, -6);
      ctx.lineTo(-2, 0);
      ctx.lineTo(1, 0);
      ctx.lineTo(-2, 6);
      ctx.stroke();
      break;
    case 'kinetic-surface-battery':
      // Crosshair: circle + cross.
      ctx.beginPath();
      ctx.arc(0, 0, 4, 0, Math.PI * 2);
      ctx.moveTo(-6, 0);
      ctx.lineTo(6, 0);
      ctx.moveTo(0, -6);
      ctx.lineTo(0, 6);
      ctx.stroke();
      break;
    case 'long-range-sensor-array':
      // Radial-spike sensor array: small central dot with eight spokes
      // radiating outward. Distinct from the crosshair (which has only
      // four cardinal lines and a thicker ring) and the lightning bolt.
      ctx.beginPath();
      ctx.arc(0, 0, 1.5, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      for (let i = 0; i < 8; i++) {
        const a = (i * Math.PI) / 4;
        ctx.moveTo(Math.cos(a) * 3, Math.sin(a) * 3);
        ctx.lineTo(Math.cos(a) * 6, Math.sin(a) * 6);
      }
      ctx.stroke();
      break;
    case 'orbital-defence-platform':
      // ODP command-structure glyph: planet disc with a 6-dot ring
      // around it, evoking the deployed ring of platforms in orbit.
      // Distinct from the LRSA radial-spike (8 spokes, no dots).
      ctx.beginPath();
      ctx.arc(0, 0, 2, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      for (let i = 0; i < 6; i++) {
        const a = (i * Math.PI) / 3;
        const dx = Math.cos(a) * 5;
        const dy = Math.sin(a) * 5;
        ctx.moveTo(dx + 0.8, dy);
        ctx.arc(dx, dy, 0.8, 0, Math.PI * 2);
      }
      ctx.fill();
      break;
  }
}

/** Shield outline used by both defence-shield glyphs. */
function drawShieldOutline(ctx: CanvasRenderingContext2D): void {
  ctx.beginPath();
  ctx.moveTo(-5, -5);
  ctx.lineTo(5, -5);
  ctx.lineTo(5, 1);
  ctx.quadraticCurveTo(5, 6, 0, 6);
  ctx.quadraticCurveTo(-5, 6, -5, 1);
  ctx.closePath();
  ctx.stroke();
}

/**
 * v2M7.9c — Constructor scaffold cage. Four right-angle bracket marks
 * forming a square cage around the build site, plus thin diagonal
 * crosshatch suggesting truss work. `fade` (0..1) drives global alpha
 * so the cage materialises over 1 s on Constructor arrival per §8.4.
 *
 * Drawn at the build site's screen position, sized to a fixed 22 px
 * square. The under-construction structure spawns into `state.ships`
 * only on completion (advance-construction.ts) so the cage is the
 * sole visual cue during the build phase.
 */
function drawScaffoldCage(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  fade: number,
  accent: string,
): void {
  const half = 11;
  const corner = 4;
  ctx.save();
  ctx.globalAlpha = fade;
  ctx.strokeStyle = accent;
  ctx.lineWidth = 1.2;
  // Four L-bracket corners.
  ctx.beginPath();
  // Top-left.
  ctx.moveTo(x - half, y - half + corner);
  ctx.lineTo(x - half, y - half);
  ctx.lineTo(x - half + corner, y - half);
  // Top-right.
  ctx.moveTo(x + half - corner, y - half);
  ctx.lineTo(x + half, y - half);
  ctx.lineTo(x + half, y - half + corner);
  // Bottom-right.
  ctx.moveTo(x + half, y + half - corner);
  ctx.lineTo(x + half, y + half);
  ctx.lineTo(x + half - corner, y + half);
  // Bottom-left.
  ctx.moveTo(x - half + corner, y + half);
  ctx.lineTo(x - half, y + half);
  ctx.lineTo(x - half, y + half - corner);
  ctx.stroke();
  // Diagonal crosshatch — two thin dashed lines suggesting truss work.
  ctx.globalAlpha = fade * 0.5;
  ctx.setLineDash([2, 2]);
  ctx.lineWidth = 0.8;
  ctx.beginPath();
  ctx.moveTo(x - half, y - half);
  ctx.lineTo(x + half, y + half);
  ctx.moveTo(x + half, y - half);
  ctx.lineTo(x - half, y + half);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.restore();
}

/**
 * v3M22.4 — paint waypoint markers for a selected ship: a dashed line
 * traces ship → waypoint[0] → waypoint[1] → … and each waypoint is a
 * small numbered dot in the empire's accent colour. Visible only
 * while the ship is selected (caller filters); never drawn for AI
 * ships.
 */
function drawWaypointMarkers(
  ctx: CanvasRenderingContext2D,
  ship: ShipInstance,
  accent: string,
  transform: (wx: number, wy: number) => { x: number; y: number },
): void {
  if (ship.waypoints.length === 0) return;
  ctx.save();
  // Dashed connecting line. MFix10.4 — when the ship has an active
  // single-shot moveOrder, that destination sits "before" the queue
  // in execution order (patrol pop only fires once moveOrder is
  // null). Originate the dashed chain at moveOrder.target so the
  // visual path reads ship → moveOrder.target → waypoint[0] →
  // waypoint[1] → … and the existing solid green moveOrder line
  // stitches up to it. Idle ships (no moveOrder) keep the original
  // ship → waypoint[0] origin.
  ctx.strokeStyle = accent;
  ctx.globalAlpha = 0.7;
  ctx.lineWidth = 1;
  ctx.setLineDash([5, 4]);
  ctx.beginPath();
  const start = ship.moveOrder
    ? transform(ship.moveOrder.targetX, ship.moveOrder.targetY)
    : transform(ship.sx, ship.sy);
  ctx.moveTo(start.x, start.y);
  for (const wp of ship.waypoints) {
    const p = transform(wp.targetX, wp.targetY);
    ctx.lineTo(p.x, p.y);
  }
  ctx.stroke();
  ctx.setLineDash([]);
  // Numbered dot per waypoint. The first waypoint reads as "1" — the
  // immediate destination.
  ctx.font = 'bold 11px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  for (let i = 0; i < ship.waypoints.length; i++) {
    const wp = ship.waypoints[i]!;
    const p = transform(wp.targetX, wp.targetY);
    ctx.globalAlpha = 0.9;
    ctx.fillStyle = accent;
    ctx.beginPath();
    ctx.arc(p.x, p.y, 6, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;
    ctx.fillStyle = 'rgba(0, 0, 0, 0.85)';
    ctx.fillText(`${i + 1}`, p.x, p.y);
  }
  ctx.restore();
}

/**
 * v3M22.4 — paint pulsing target rings on every valid Guard target in
 * the focused system: every planet (any owner) and every player-owned
 * ship except the guarding ship itself. Ring colour is the guarding
 * empire's accent; alpha pulses on a 1-second cycle. The player
 * commits a target with LMB (handled in input.ts); this is purely
 * visual feedback.
 */
function drawGuardTargetRings(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  ui: UiState,
  layout: SystemViewLayout,
): void {
  if (ui.guardTargetMode === null) return;
  const mode = ui.guardTargetMode;
  const guardingShip = state.ships.get(mode.shipId);
  if (!guardingShip) return;
  const empire = state.empires.get(guardingShip.ownerEmpireId);
  const accent = empire?.color ?? '#ffe28a';
  // 10-tick (1 s @ 10 Hz) triangle wave for alpha pulse.
  const phase = (state.tick % 10) / 10;
  const tri = phase < 0.5 ? phase * 2 : (1 - phase) * 2;
  const alpha = 0.35 + 0.4 * tri;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.strokeStyle = accent;
  ctx.lineWidth = 2;
  ctx.setLineDash([4, 3]);
  // Planets in this system. Layout's pre-computed (x, y, r) are the
  // screen-space disc centre + radius, so the ring just adds 8px clearance.
  for (const planetLayout of layout.planets) {
    ctx.beginPath();
    ctx.arc(planetLayout.x, planetLayout.y, planetLayout.r + 8, 0, Math.PI * 2);
    ctx.stroke();
  }
  // Friendly ships in this system, excluding the guarding ship itself.
  for (const shipLayout of layout.ships) {
    const ship = shipLayout.ship;
    if (ship.id === mode.shipId) continue;
    if (ship.ownerEmpireId !== state.playerEmpireId) continue;
    ctx.beginPath();
    ctx.arc(shipLayout.x, shipLayout.y, 14, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.setLineDash([]);
  ctx.restore();
}
